import { useCallback, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { PageHeader } from '../../layouts/Shells';
import { ProjectBoardView } from './ProjectBoardView';
import { searchProjects } from './projectBoard';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Skeleton,
} from '../../components/ui';
import { SectionRow, SectionRows } from '../../components/ui/SectionRow';
import { useWorkspaceSettings } from '../settings/hooks';
import { useAuth } from '../auth/AuthProvider';
import { formatDate } from '../listView/TaskTable';
import { localIsoDate } from '../myTasks/types';
import { ProjectCreateDialog } from './ProjectCreateDialog';
import type { ProjectWorkflowStatus } from './types';
import {
  useCreateProjectCategory,
  useMoveProjectCategory,
  useProjectCategories,
  useProjects,
} from './hooks';
import {
  PROJECT_WORKFLOW_LABELS,
  VISIBILITY_LABELS,
  canCreateProject,
  groupProjectsByCategory,
  knownCategories,
  normalizeCategory,
} from './types';
import type { ProjectListItem } from './types';
import './projectStatus.css';

function isPermissionError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return message.includes('permission denied') || message.includes('not authorized');
}

function todayIso(): string {
  return localIsoDate(new Date());
}

/** Dot color per workflow state — quiet text instead of a pill. */
const STATUS_DOT_TONES: Record<ProjectListItem['status'], string> = {
  not_started: '',
  in_progress: 'hub-status--brand',
  // Its own tone, not shared with revision. The two words differ by one
  // letter and sit next to each other, so the dot is doing real work.
  for_review: 'hub-status--review',
  for_revision: 'hub-status--revision',
  on_hold: 'hub-status--warning',
  completed: 'hub-status--success',
};

const PROJECT_DRAG_MIME = 'application/x-tap-project';

function ProjectRow({ project, draggable }: { project: ProjectListItem; draggable: boolean }) {
  return (
    <li className="project-row">
      {draggable ? (
        <span
          className="task-grip project-grip"
          draggable
          role="button"
          tabIndex={-1}
          aria-label={`Move ${project.name} to another category`}
          title="Drag to another category"
          onDragStart={(event) => {
            event.dataTransfer.setData(PROJECT_DRAG_MIME, project.id);
            event.dataTransfer.effectAllowed = 'move';
          }}
        >
          {/* The row holds a link; making the row itself draggable would fight
              the link's own drag behaviour, so only the grip drags. */}
          ⋮⋮
        </span>
      ) : null}
      {/* Semibold, body size, primary. The category anchor above it is LARGER
          and lighter-weight — the anti-pattern the brief names is two bolds of
          the same size stacked, where neither reads as the parent. */}
      <Link className="project-row-link text-subsection" to={`/projects/${project.id}`}>
        {project.name}
      </Link>
      {/* Both classes: hub-status keeps the tone dot (its modifiers style the
          ::before), status-pill adds the muted chip around it. */}
      <span className={`hub-status status-pill ${STATUS_DOT_TONES[project.status]}`.trim()}>
        {PROJECT_WORKFLOW_LABELS[project.status]}
      </span>
      {project.visibility === 'members_only' && (
        <Badge tone="neutral">{VISIBILITY_LABELS[project.visibility]}</Badge>
      )}
      {project.due_date ? (
        <span
          className={
            project.due_date < todayIso() && project.status !== 'completed'
              ? 'my-task-due my-task-due--overdue'
              : 'my-task-due'
          }
          style={{ marginLeft: 'auto' }}
        >
          Due {formatDate(project.due_date)}
        </span>
      ) : null}
    </li>
  );
}

export function ProjectsPage() {
  // A task with no project still gets a /projects/?task=<id> link from the
  // agent API, and an empty project id lands on this list, which has no task
  // to open. Those tasks are sales tasks, so send the link where they open.
  // Same shape as TrackerPage's old-tab redirects.
  const [redirectParams] = useSearchParams();
  const taskId = redirectParams.get('task');
  if (taskId !== null && taskId !== '') {
    return <Navigate to={`/sales-tasks?task=${encodeURIComponent(taskId)}`} replace />;
  }

  return <ProjectsList />;
}

function ProjectsList() {
  const { access } = useAuth();
  const workspace = useWorkspaceSettings();
  const projects = useProjects();
  const curated = useProjectCategories();
  const createCategory = useCreateProjectCategory();
  // Empty set means "no status filter", which keeps the default view free of
  // an All chip that has to stay in sync with the others.
  const [statusFilter, setStatusFilter] = useState<Set<ProjectWorkflowStatus>>(new Set());
  // Completed work is finished work: it belongs on the page, not in the way.
  // Collapsed by default; the toggle lasts as long as the page is open.
  const [completedOpen, setCompletedOpen] = useState(false);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);
  const [moveError, setMoveError] = useState('');
  const moveCategory = useMoveProjectCategory();
  const completedRef = useRef<HTMLElement | null>(null);
  const [creating, setCreating] = useState(false);
  const [createInCategory, setCreateInCategory] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [categoryError, setCategoryError] = useState('');
  /**
   * List or Board, and which category the board is narrowed to.
   *
   * Local to the page rather than in a saved view: /projects has no saved
   * views, and this is how you are looking rather than what you are looking
   * at. The category is held here, not inside the board, so switching to List
   * and back does not lose it.
   */
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [boardCategory, setBoardCategory] = useState<string | undefined>(undefined);
  /**
   * Free text, shared by both views. Held here rather than inside either one
   * so a search survives the List | Board switch — otherwise finding something
   * on the board and flipping to List to work on it loses it.
   */
  const [query, setQuery] = useState('');
  // Stable so the creation dialog's focus trap is not rebuilt on every render.
  const closeCreate = useCallback(() => {
    setCreating(false);
    setCreateInCategory('');
  }, []);
  const closeAddCategory = useCallback(() => {
    setAddingCategory(false);
    setNewCategory('');
    setCategoryError('');
  }, []);

  // The picker offers curated categories plus anything already in use,
  // one entry per name regardless of case.
  const inUse = knownCategories(projects.data ?? []);
  const allCategories = [...(curated.data ?? [])];
  for (const name of inUse) {
    if (!allCategories.some((known) => known.toLocaleLowerCase() === name.toLocaleLowerCase())) {
      allCategories.push(name);
    }
  }
  allCategories.sort((left, right) => left.localeCompare(right));

  function submitNewCategory() {
    const clean = newCategory.trim();
    if (clean === '') {
      setCategoryError('Type a category name first.');
      return;
    }
    if (normalizeCategory(clean, allCategories) !== clean || allCategories.includes(clean)) {
      const existing = normalizeCategory(clean, allCategories);
      if (allCategories.some((known) => known.toLocaleLowerCase() === clean.toLocaleLowerCase())) {
        setCategoryError(`"${existing}" already exists.`);
        return;
      }
    }
    setCategoryError('');
    createCategory.mutate(clean, {
      onSuccess: closeAddCategory,
      onError: (cause) =>
        setCategoryError(
          cause.message === 'category_exists'
            ? 'That category already exists.'
            : 'The category could not be added. Try again.',
        ),
    });
  }

  const profile = access.status === 'approved' ? access.profile : null;
  const allowMembers = workspace.data?.allow_member_project_creation;
  const mayCreate = profile ? canCreateProject(profile, allowMembers) : false;
  // Blocked by the workspace switch rather than by their role. Worth telling
  // them apart: a viewer never had the button, where a member watched it
  // disappear and has no way to know an admin turned it off.
  const blockedBySetting = profile !== null && !mayCreate && canCreateProject(profile);

  function body() {
    if (projects.isPending) {
      return <Skeleton lines={4} label="Loading projects" />;
    }
    if (projects.isError) {
      return isPermissionError(projects.error) ? (
        <EmptyState
          title="You do not have access to projects"
          description="Ask a TAP admin if you believe this is a mistake."
        />
      ) : (
        <EmptyState
          title="Projects could not be loaded"
          description="Something went wrong. Try again in a moment."
          action={<Button onClick={() => void projects.refetch()}>Retry</Button>}
        />
      );
    }
    if (projects.data.length === 0) {
      return (
        <EmptyState
          title="No projects yet"
          description={
            mayCreate
              ? 'Create the first project to get started.'
              : 'Projects shared with you will appear here.'
          }
        />
      );
    }
    // Search narrows what BOTH views work from. The status chips and category
    // pills then narrow further; each is a separate question and they compose.
    const found = searchProjects(projects.data, query);

    // Counts come from the FULL list, not the filtered one, so a chip never
    // reads zero purely because it is switched off.
    const statusCounts = new Map<ProjectWorkflowStatus, number>();
    for (const project of projects.data) {
      statusCounts.set(project.status, (statusCounts.get(project.status) ?? 0) + 1);
    }
    const visible =
      statusFilter.size === 0 ? found : found.filter((project) => statusFilter.has(project.status));

    // Completed projects leave the category lists entirely and gather in one
    // section at the bottom. The status chips filter the ACTIVE area only —
    // the completed section always holds every completed project, so turning
    // the Completed chip on cannot empty the page and look broken.
    const completed = found.filter((project) => project.status === 'completed');
    const groups = groupProjectsByCategory(
      visible.filter((project) => project.status !== 'completed'),
    );
    // Curated categories appear even while empty, so the flow "add the
    // category, then add projects into it" works in that order.
    for (const name of allCategories) {
      if (
        !groups.some((group) => group.category.toLocaleLowerCase() === name.toLocaleLowerCase())
      ) {
        const uncategorizedAt = groups.findIndex((group) => group.category === '');
        const empty = { category: name, projects: [] };
        if (uncategorizedAt === -1) {
          groups.push(empty);
        } else {
          groups.splice(uncategorizedAt, 0, empty);
        }
      }
    }
    groups.sort((left, right) => {
      if (left.category === '') return 1;
      if (right.category === '') return -1;
      return left.category.localeCompare(right.category);
    });
    const searchBox = (
      <div className="project-search">
        <Input
          label="Search projects"
          labelHidden
          type="search"
          placeholder="Search name, description or category…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {query.trim() === '' ? null : (
          <span className="project-search-count">
            {found.length} of {projects.data.length}
          </span>
        )}
      </div>
    );

    if (viewMode === 'board') {
      // The board reads the full list. The status chips above the LIST are a
      // different control with a different job — on a board the statuses are
      // the columns, so filtering them away would delete the layout.
      return (
        <>
          {searchBox}
          <ProjectBoardView
            projects={found}
            category={boardCategory}
            onCategoryChange={setBoardCategory}
          />
        </>
      );
    }

    const onlyUncategorized = groups.length === 1 && groups[0]?.category === '';
    return (
      <>
        {searchBox}
        <div className="project-status-filter" role="group" aria-label="Filter projects by status">
          {(Object.keys(PROJECT_WORKFLOW_LABELS) as ProjectWorkflowStatus[]).map((status) => {
            const on = statusFilter.has(status);
            return (
              <button
                key={status}
                type="button"
                className={`status-chip${on ? ' status-chip--on' : ''}`}
                aria-pressed={on}
                onClick={() => {
                  setStatusFilter((current) => {
                    const next = new Set(current);
                    if (next.has(status)) {
                      next.delete(status);
                    } else {
                      next.add(status);
                    }
                    return next;
                  });
                  // Completed projects are not in the lists above, so a chip
                  // that only filtered would appear to do nothing. Opening the
                  // section and scrolling to it is what the click means.
                  if (status === 'completed') {
                    setCompletedOpen(true);
                    // After paint: the section has to be expanded before it can
                    // be scrolled to.
                    requestAnimationFrame(() => {
                      completedRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    });
                  }
                }}
              >
                {PROJECT_WORKFLOW_LABELS[status]} ({statusCounts.get(status) ?? 0})
              </button>
            );
          })}
          {statusFilter.size > 0 && (
            <button
              type="button"
              className="status-chip status-chip--clear"
              onClick={() => setStatusFilter(new Set())}
            >
              Clear
            </button>
          )}
        </div>
        {visible.length === 0 && completed.length === 0 ? (
          <EmptyState
            title="No projects match this filter"
            description="Clear the filter to see everything again."
          />
        ) : null}
        {moveError === '' ? null : <InlineError>{moveError}</InlineError>}
        {/* One rhythm down the page, and every row a DIRECT child of
            SectionRows — which is what the three rules that draw that rhythm
            require. This used to wrap each row in a bare <section> to hold the
            drag handlers, and the wrapper made every row a grandchild:
            `:scope > .section-row` matched zero elements, so the dividers
            between categories never rendered and the first and last rows kept
            padding they should not have had. SectionRow takes the handlers
            itself now; the drop zone is still the whole category including its
            empty space, because the row IS the category. */}
        <SectionRows>
          {groups.map((group) => (
            <SectionRow
              key={group.category === '' ? '(none)' : group.category}
              className={dragOverCategory === group.category ? 'project-category--drop' : undefined}
              onDragOver={
                !mayCreate
                  ? undefined
                  : (event) => {
                      if (!event.dataTransfer.types.includes(PROJECT_DRAG_MIME)) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      setDragOverCategory(group.category);
                    }
              }
              onDragLeave={() => setDragOverCategory(null)}
              onDrop={
                !mayCreate
                  ? undefined
                  : (event) => {
                      event.preventDefault();
                      setDragOverCategory(null);
                      const projectId = event.dataTransfer.getData(PROJECT_DRAG_MIME);
                      const moving = projects.data?.find((row) => row.id === projectId);
                      // Dropping a project back into the category it is already
                      // in is not a write.
                      if (!moving || moving.category === group.category) return;
                      moveCategory.mutate(
                        { projectId, category: group.category },
                        { onError: () => setMoveError('That project could not be moved.') },
                      );
                    }
              }
              title={
                onlyUncategorized
                  ? 'Projects'
                  : `${group.category === '' ? 'Uncategorized' : group.category}`
              }
              subtitle={`${String(group.projects.length)} ${
                group.projects.length === 1 ? 'project' : 'projects'
              }`}
              action={
                mayCreate && group.category !== '' && !onlyUncategorized ? (
                  /* Anchored beside the section it belongs to rather than
                     dangling under the list. "New project here" under a list of
                     nine projects reads as a tenth row. */
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCreateInCategory(group.category);
                      setCreating(true);
                    }}
                  >
                    + Add
                  </Button>
                ) : undefined
              }
            >
              <ul className="project-list">
                {group.projects.length === 0 ? (
                  <li className="project-row">
                    <span className="text-body-meta project-category-empty">
                      No projects in this category yet.{' '}
                      {mayCreate && group.category !== '' && !onlyUncategorized ? (
                        <Link
                          to="/projects"
                          aria-haspopup="dialog"
                          onClick={(event) => {
                            event.preventDefault();
                            setCreateInCategory(group.category);
                            setCreating(true);
                          }}
                        >
                          Add project
                        </Link>
                      ) : null}
                    </span>
                  </li>
                ) : (
                  group.projects.map((project) => (
                    <ProjectRow key={project.id} project={project} draggable={mayCreate} />
                  ))
                )}
              </ul>
            </SectionRow>
          ))}
        </SectionRows>

        {completed.length === 0 ? null : (
          <section className="completed-projects" ref={completedRef}>
            <button
              type="button"
              className="completed-projects-toggle"
              aria-expanded={completedOpen}
              onClick={() => setCompletedOpen((open) => !open)}
            >
              <span aria-hidden="true">{completedOpen ? '▾' : '▸'}</span>
              <span>✔ Completed Projects ({completed.length})</span>
            </button>
            {completedOpen ? (
              <ul className="project-list completed-projects-list">
                {completed.map((project) => (
                  // Finished work does not get dragged between categories.
                  <ProjectRow key={project.id} project={project} draggable={false} />
                ))}
              </ul>
            ) : null}
          </section>
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader title="Projects" topbar />
      <Panel
        actions={
          <span className="review-actions">
            <span className="view-toggle" role="group" aria-label="View mode">
              <button
                type="button"
                className="view-toggle-button"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
              <button
                type="button"
                className="view-toggle-button"
                aria-pressed={viewMode === 'board'}
                onClick={() => setViewMode('board')}
              >
                Board
              </button>
            </span>
            {/* TAP Viewers are never offered creation. The database refuses
                them regardless of what the interface shows. Everyone gets the
                view toggle: looking is not creating. */}
            {mayCreate ? (
              <>
                <Button variant="secondary" onClick={() => setAddingCategory(true)}>
                  Add category
                </Button>
                <Button onClick={() => setCreating(true)}>New project</Button>
              </>
            ) : blockedBySetting ? (
              <span className="text-body-meta">
                A TAP admin has turned off project creation for members.
              </span>
            ) : null}
          </span>
        }
      >
        {body()}
      </Panel>
      {mayCreate ? (
        <ProjectCreateDialog
          open={creating}
          onClose={closeCreate}
          categories={allCategories}
          initialCategory={createInCategory}
        />
      ) : null}
      {mayCreate ? (
        <Dialog
          open={addingCategory}
          title="Add category"
          onClose={closeAddCategory}
          footer={
            <>
              <Button variant="secondary" onClick={closeAddCategory}>
                Cancel
              </Button>
              <Button onClick={submitNewCategory} disabled={createCategory.isPending}>
                {createCategory.isPending ? 'Adding…' : 'Add category'}
              </Button>
            </>
          }
        >
          {categoryError === '' ? null : <InlineError>{categoryError}</InlineError>}
          <Input
            label="Category name"
            value={newCategory}
            placeholder="Social Media, Corp Gift, Sales…"
            onChange={(event) => setNewCategory(event.target.value)}
          />
        </Dialog>
      ) : null}
    </>
  );
}
